import { showErrorToast, showToast } from '@lingoda/toasts';
import { trans } from '@lingoda/i18n';
import {
    type ActivityChallengeParticipantStatusEnum,
    type BackgroundThemeEnum,
    getApolloErrorUiMessage,
} from '@lingoda/monolith-graphql';
import {
    Box,
    Button,
    FramePageContainer,
    Hidden,
    Stack,
    Typography,
    useResolution,
} from '@lingoda/ui';
import { studentChallengePath } from '@lingoda/router';
import { getChallengeStatus, getChallengeStatusText, getChallengeTexts } from '../../utils';
import { useJoinActivityChallenge } from '../../hooks';
import {
    ChallengeParticipationStatus,
    DesktopBackgroundImageStack,
    MobileBackgroundImageBox,
} from '../TopSection';
import { JoinButton } from '../JoinButton';

interface Props {
    challengeName: string;
    startDate: string;
    endDate: string;
    publicName: string;
    participants: number;
    userStatus: ActivityChallengeParticipantStatusEnum | undefined;
    desktopImageUrl: string | undefined;
    mobileImageUrl: string | undefined;
    desktopBackgroundTheme: BackgroundThemeEnum;
    mobileBackgroundTheme: BackgroundThemeEnum;
}

export const ActiveChallengeWidgetUnjoined = (props: Props) => {
    const { challengeName, startDate, endDate, participants } = props;
    const title = props.publicName;
    const { subtitle } = getChallengeTexts(challengeName, startDate);
    const status = getChallengeStatus(startDate, endDate);
    const statusText = getChallengeStatusText(status, startDate, endDate);

    const isMobile = useResolution('phone');
    const theme = isMobile ? props.mobileBackgroundTheme : props.desktopBackgroundTheme;

    const [joinChallenge, { loading: joiningLoading }] = useJoinActivityChallenge({
        challengeName,
        userStatus: props.userStatus,
        onSuccess() {
            showToast('success', trans('btn-challenge-joined', {}, 'student-challenges'));
        },
        onError(error) {
            showErrorToast(getApolloErrorUiMessage(error) || trans('masked-error', {}, 'errors'));
        },
    });

    return (
        // Wrap with extra Box to reset parent-applied margins from Stack spacing
        <Box>
            <Box position="relative" mx={{ xs: -2, sm: 0 }}>
                <Hidden smUp>
                    <MobileBackgroundImageBox imageUrl={props.mobileImageUrl} />
                </Hidden>
                <FramePageContainer px={{ xs: 2, sm: 0 }} pt={{ xs: 28, smn: 32, sm: 0 }}>
                    <DesktopBackgroundImageStack
                        themeMode={theme}
                        desktopImageUrl={props.desktopImageUrl}
                        justifyContent={{ xs: 'normal', sm: 'center' }}
                    >
                        <Typography
                            variant="subtitle1"
                            textTransform="uppercase"
                            sx={({ colors }) => ({
                                color: themed(theme, colors.text.subtle, colors.text.inverseSubtle),
                            })}
                        >
                            {subtitle}
                        </Typography>
                        <Typography variant="h1" maxWidth={{ sm: 370 }}>
                            {title}
                        </Typography>
                        <ChallengeParticipationStatus
                            statusText={statusText}
                            participants={participants}
                        />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} width="100%">
                            <JoinButton
                                onJoinClick={joinChallenge}
                                joined={false}
                                joiningLoading={joiningLoading}
                                status={status}
                                theme={theme}
                            />
                            <Button
                                variant="text"
                                inverted={theme === 'dark'}
                                href={studentChallengePath({ challengeName })}
                            >
                                {trans('btn-see-details', {}, 'student-challenges')}
                            </Button>
                        </Stack>
                    </DesktopBackgroundImageStack>
                </FramePageContainer>
            </Box>
        </Box>
    );
};

function themed(theme: 'light' | 'dark', light: string, dark: string) {
    return theme === 'dark' ? dark : light;
}
